import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Clock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingMessage, setPendingMessage] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setPendingMessage('');
    try {
      const data = await login(email, password);
      toast.success('Welcome back!');
      if (data.user.admin_access) navigate('/admin');
      else navigate('/');
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiError(err, 'Login failed');
      if (status === 403) {
        setPendingMessage(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pw-container py-12">
      <div className="max-w-md mx-auto">
        <h1 className="text-xl font-semibold tracking-wider uppercase mb-6 text-center">Sign In</h1>
        {pendingMessage && (
          <div data-testid="pending-approval-banner" className="flex items-start gap-3 bg-[#fef8e8] border border-[#e8d89c] rounded-lg p-4 mb-5">
            <Clock size={18} className="text-[#b28a4a] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[#8a6d2b] mb-0.5">Account Pending Approval</p>
              <p className="text-xs text-[#8a6d2b]/80">{pendingMessage}</p>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="login-email" className="text-xs text-[#6f6a63] block mb-1">Email</label>
            <input id="login-email" data-testid="login-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]" />
          </div>
          <div>
            <label htmlFor="login-password" className="text-xs text-[#6f6a63] block mb-1">Password</label>
            <div className="relative">
              <input id="login-password" data-testid="login-password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-3 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f] pr-11" />
              <button type="button" data-testid="login-toggle-password" onClick={() => setShowPassword(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6f6a63] hover:text-[#2d2d2d] transition-colors p-0.5" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="text-right">
              <Link to="/forgot-password" className="text-xs text-[#2f6b4f] underline">Forgot your password?</Link>
            </div>
          </div>
          <button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-[#2d2d2d] text-white py-3 rounded-lg text-sm tracking-wider uppercase font-medium hover:bg-[#232323] disabled:opacity-50">
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <p className="text-center text-sm text-[#6f6a63] mt-6">Don't have an account? <Link to="/register" className="text-[#2f6b4f] font-medium">Register</Link></p>
      </div>
    </div>
  );
}
